import { useEffect } from 'react';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../application';

import { DynamicEntityCMSDataContext, DynamicEntityStoreProvider, useDynamicEntityStoreContext } from './contexts';
import { ERRORS, generateFullDefinitions } from './lib';
import { authorisedGetImages } from '../images/lib';

export default function BuilderWrapper(props) {
  const { isModule = false, dynamicEntity, children } = props;

  return (
    <QueryClientProvider client={queryClient}>
      <DynamicEntityStoreProvider dynamicEntity={dynamicEntity} isModule={isModule}>
        <BuilderWrapperContent {...props}>{children}</BuilderWrapperContent>
      </DynamicEntityStoreProvider>
    </QueryClientProvider>
  );
}

function BuilderWrapperContent(props) {
  const {
    isTemplate,
    isModule = false,
    entityTitle,
    allSitePages,
    categoriesWithTemplates,
    categoriesLinked,
    componentDefinitions,
    endpoints,
    forms,
    dynamicPageModules,
    siteLocales,
    jobSchema,
    jobCategories,
    children,
  } = props;

  const activeTab = useDynamicEntityStoreContext((state) => state.activeTab);
  const update = useDynamicEntityStoreContext((state) => state.update);
  const title = useDynamicEntityStoreContext((state) => state.data.title);
  const url_slug = useDynamicEntityStoreContext((state) => state.data.url_slug);

  const {
    data: images,
    isLoading: isLoadingImages,
    isError: isErrorImages,
    error: imagesError,
  } = useQuery({
    queryFn: async () => {
      const response = await authorisedGetImages();
      return response.data;
    },
    queryKey: ['builderWrapper', 'images'],
    enabled: activeTab === 1 || activeTab === 2, // Only run query when in SEO and Content tabs
    keepPreviousData: true,
  });

  useEffect(() => {
    const validatePage = () => {
      const errorKeys = [];

      if (!title) {
        errorKeys.push(ERRORS[0].key);
      }

      if (!url_slug && !isModule) {
        errorKeys.push(ERRORS[1].key);
      }

      if (url_slug && url_slug !== '/' && /(^\/+|\/+$|\s)/.test(url_slug) && !isModule) {
        errorKeys.push(ERRORS[2].key);
      }

      const isError = errorKeys.length > 0;

      update({
        isError,
        errorKeys,
      });
    };

    validatePage();
  }, [title, url_slug, isModule, update]);

  return (
    <DynamicEntityCMSDataContext.Provider
      value={{
        isTemplate,
        isModule,
        entityTitle,
        allSitePages,
        categoriesWithTemplates,
        categoriesLinked,
        componentDefinitions: generateFullDefinitions(componentDefinitions, isTemplate),
        endpoints,
        forms,
        dynamicPageModules,
        siteLocales,
        jobSchema,
        jobCategories,

        // Images
        images,
        isLoadingImages,
        isErrorImages,
        imagesError,
      }}
    >
      {children}
    </DynamicEntityCMSDataContext.Provider>
  );
}
